import _isFunction from 'lodash/isFunction'
import _toNumber from 'lodash/toNumber'
import { Map, List } from 'immutable'
import {
  PLAY_CLICK_GA4_EVENT,
  CLICK_TO_DETAILS_PAGE_GA4_EVENT,
  TAG_CLICKED_GA4_EVENT,
} from 'services/event-tracking'
import {
  TYPE_CONTENT_PRODUCT,
  TYPE_CONTENT_SERIES,
  TYPE_CONTENT_VIDEO,
  TYPE_CONTENT_PERSON,
  TYPE_CONTENT_TAG,
} from 'services/content-type'
import {
  createUpstreamContext,
  upstreamContextOnClick,
} from 'services/upstream-context'
import { getPathLocationName, isMemberHome, isSearch } from 'services/url'
import { NODE_TAG_TYPE_NEW } from 'services/node/utils'
import {
  CONTENT_IMAGE_TYPE_KEYART_WITH_TEXT,
  CONTENT_IMAGE_TYPE_KEYART_NO_TEXT,
  CONTENT_IMAGE_TYPE_POSTER,
  CONTENT_IMAGE_TYPE_POSTER_3X4,
  CONTENT_IMAGE_TYPE_HEADSHOT,
  getFeatureMedia,
} from 'services/content'
import { isLiveAccessFeature } from 'services/live-access-feature'
import { hasLiveAccessEntitlement } from 'services/subscription'
import {
  DISPLAY_MODE_TILE_LIST_3X4,
  DISPLAY_MODE_TILE_LIST_3X4_BLUE_GRADIENT_BACKGROUND,
  DISPLAY_MODE_TILE_LIST_3X4_GRAY_BACKGROUND,
  DISPLAY_MODE_TILE_LIST_5X7,
  DISPLAY_MODE_TILE_LIST_5X7_BLUE_GRADIENT_BACKGROUND,
  DISPLAY_MODE_TILE_LIST_5X7_GRAY_BACKGROUND,
  DISPLAY_MODE_TILE_LIST_TOP_TEN_3X4,
  DISPLAY_MODE_TILE_LIST_GAIA_PLUS,
} from 'services/pm-section'
import {
  TILE_V2_DISPLAY_VERTICAL,
  TILE_V2_DISPLAY_DEFAULT,
  TILE_V2_DISPLAY_POSTER,
} from './constants'

/**
 * Click handler for a tile click
 * @param {Object} props - The props
 * @param {Map} props.data - The content data
 * @param {String} props.contentType - The type of content
 * @param {String} props.pageLanguage - The language that the page is displayed in, such as "en"
 *  for sending the default GA event
 * @param {Boolean} fullplayer - Whether or not this click is coming from a fullplayer link
 */
export function handleTileOnClickGA (props, fullplayer = false, uiElement) {
  const {
    data,
    contentType,
    setDefaultGa4Event,
    rowPlacement = null,
    index = null,
    adminTitle,
    userNodeInfo,
    uiLocation,
    isLoggedIn,
    resolver = Map(),
    useGraphQLData,
  } = props
  const currentPagePath = resolver.get('path')
  const isMemberHomePage = isMemberHome(currentPagePath, isLoggedIn)
  const isSearchPage = isSearch(currentPagePath)
  const id = data.get('id') || data.get('contentId')

  const ga4EventData = {
    content_id: id,
    content_name: data.get('title'),
    content_type: contentType,
    media_language: data.get('mediaLanguage') || data.get('languageFullText'),
    ui_location: uiLocation,
    ui_element: uiElement || 'Tile',
  }

  const isSeries = contentType === TYPE_CONTENT_SERIES
  let userNodeMediaId

  if (useGraphQLData) {
    const featuredMedia = getFeatureMedia(props)
    userNodeMediaId = featuredMedia.get('mediaId')
  } else {
    userNodeMediaId = isSeries ? userNodeInfo.getIn(['featuredEpisode', 'data', 'mediaId']) : userNodeInfo.getIn(['data', 'media', 'id'])
  }
  const mediaId = _toNumber(userNodeMediaId || data.getIn(['featuredEpisode', 'data', 'mediaId']) || data.getIn(['feature', 'id']) || data.getIn(['featureMedia', 'mediaId']) || 0)
  const parentSeriesId = isSeries ? id : data.get('seriesId')
  const rowData = isMemberHomePage ? {
    row_placement: rowPlacement,
    tile_number: index,
    row_name: adminTitle,
  } : {}
  const clickAttributionData = isMemberHomePage || isSearchPage ? {
    click_attribution: isMemberHomePage ? 'Member Home' : 'Search',
  } : {}
  const parentSeriesData = parentSeriesId ? {
    parent_series_name: isSeries ? data.get('title') : data.get('seriesTitle'),
    parent_series_id: parentSeriesId,
  } : {}

  // GA4 events
  if (fullplayer) {
    const playClickEventData = PLAY_CLICK_GA4_EVENT
      .mergeDeep({
        ...ga4EventData,
        ...parentSeriesData,
        ...rowData,
        ...clickAttributionData,
        media_id: mediaId,
        is_featured_media: true,
        ui_element: 'Tile',
      })
    setDefaultGa4Event(playClickEventData)
  } else if (contentType === TYPE_CONTENT_VIDEO) {
    const videoDetailsEventData = CLICK_TO_DETAILS_PAGE_GA4_EVENT
      .mergeDeep({
        ...ga4EventData,
        ...parentSeriesData,
        ...clickAttributionData,
        ...rowData,
        media_id: mediaId,
        is_featured_media: true,
      })
    setDefaultGa4Event(videoDetailsEventData)
  } else if (contentType === TYPE_CONTENT_SERIES) {
    const seriesDetailsEventData = CLICK_TO_DETAILS_PAGE_GA4_EVENT
      .mergeDeep({
        ...rowData,
        ...ga4EventData,
        ...clickAttributionData,
        is_featured_media: true,
      })
    setDefaultGa4Event(seriesDetailsEventData)
  } else if (contentType === TYPE_CONTENT_PRODUCT) {
    const productDetailsEventData = CLICK_TO_DETAILS_PAGE_GA4_EVENT.mergeDeep({
      ...ga4EventData,
      ...clickAttributionData,
    })
    setDefaultGa4Event(productDetailsEventData)
  }
}

/**
 * The url for the tile
 * @param {Object} props - The props
 * @param {Map} props.data - The content data
 * @param {Boolean} props.isLoggedIn - Whether or not the user is logged in
 * @param {String} props.contentType - The type of content
 * @returns {String} The url for the tile
 */
export function getTileUrl (props) {
  const { data = Map(), isLoggedIn, contentType } = props
  const isTypeProduct = contentType === TYPE_CONTENT_PRODUCT
  let url = data.get('url', '')

  // get the url for the product, as it lives in a different placed in the data
  if (isLoggedIn && isTypeProduct) {
    url = data.getIn(['product', 'url'])
  }

  return url
}

/**
 * Creates the upstream context object for member home
 * @param {Object} props - The props
 * @param {Number} props.rowPlacement - The index of the row
 * @param {String} props.adminTitle - The admin title
 * @param {Number} props.index - The index of the tile
 * @returns {Object} The upstream context object for member home
 */
export function buildMhTileUpstreamContext (props) {
  const {
    rowPlacement,
    adminTitle,
    index,
  } = props

  const mhTileData = {
    tileNumber: index,
    rowPlacement,
    row_name: adminTitle,
  }

  return createUpstreamContext({ mhTileData })
}

/**
 * Returns tile data object for member home
 * @param {Object} props - The props
 * @param {Number} props.rowPlacement - The index of the row
 * @param {String} props.adminTitle - The admin title
 * @param {Number} props.index - The index of the tile
 * @returns {Object} The tile data object for member home
 */
function getMhTileData (props) {
  const {
    rowPlacement,
    adminTitle,
    isLoggedIn,
    resolver,
    index,
  } = props

  const currentPagePath = resolver.get('path')
  const isMemberHomePage = isMemberHome(currentPagePath, isLoggedIn)
  const isSearchPage = isSearch(currentPagePath)

  if (isMemberHomePage || isSearchPage) {
    return {
      tileNumber: index,
      rowPlacement,
      rowName: adminTitle,
      clickAttribution: isMemberHomePage ? 'Member Home' : 'Search',
    }
  }

  return null
}

/**
 * Handler for upstreamContext
 * @param {Object} e - The click event object
 * @param {Object} props - The props
 * @param {Boolean} isFullplayer - Whether or not the link that was clicked is a fullplayer link
 * @param {Map} props.upstreamContext - The upstream context
 */
export function handleUpstreamContext (e, props, isFullplayer = false) {
  const {
    setUpstreamContext,
    upstreamContext,
  } = props
  let updatedContext = upstreamContext
  const mhTileData = getMhTileData(props) || null

  if (mhTileData) {
    if (isFullplayer) {
      updatedContext = upstreamContext.merge({ playEventData: mhTileData })
    } else {
      updatedContext = createUpstreamContext({ mhTileData })
    }
  }
  upstreamContextOnClick(e, { upstreamContext: updatedContext, setUpstreamContext })
}

/**
 * The click handler for tile v2
 * @param {Object} e - The click event object
 * @param {*} props - The props
 * @param {*} fullplayer - Whether or not the link that was clicked is a fullplayer link
 */
export function handleTileV2OnClick (e, props, fullplayer = false, uiElement) {
  const { onClick } = props

  if (onClick && _isFunction(onClick)) {
    onClick()
  }

  handleTileOnClickGA(props, fullplayer, uiElement)
  handleUpstreamContext(e, props, fullplayer)
}

/**
 * Determine if the studio is "Gaia"
 * @param {Object} props - The props
 * @returns {Boolean} Whether or not the piece of content is a "Gaia original",
 *  based on the studio value
 */
export function isGaiaOriginal (props) {
  const { studio } = props
  const gaiaOriginalRegex = /^gaia$/i

  if (gaiaOriginalRegex.test(studio)) {
    return true
  }

  return false
}

/**
 * Get the hero image for a tile
 * @param {Object} props - The props
 * @param {Map} props.data - The content data
 * @param {String} props.display - The display type for the content
 * @param {String} props.contentType - The type of content
 * @returns {String} the url for the hero image
 */
export function getHeroImage (props) {
  const {
    data = Map(),
    display = TILE_V2_DISPLAY_DEFAULT,
    contentType,
  } = props
  const vertical = display === TILE_V2_DISPLAY_VERTICAL
  let image = data.get('image', '')

  if (vertical && data.get('verticalImage')) {
    image = data.get('verticalImage', '')
  }

  if (contentType === TYPE_CONTENT_PERSON) {
    image = data.get('headshot', '')
  }

  if (contentType === TYPE_CONTENT_TAG) {
    image = data.get('imageClean', '')
  }

  return image
}

/**
 * Get the hero image for a tile from Graphql data
 * @param {Object} props - The props
 * @param {Map} props.data - The content data
 * @param {String} props.display - The display type for the content
 * @param {String} props.contentType - The type of content
 * @returns {String} the url for the hero image
 */
export function getGraphqlHeroImage (props) {
  const {
    data = Map(),
    display = TILE_V2_DISPLAY_DEFAULT,
    contentType,
  } = props
  const images = data.get('images', List()) || List()
  let image = images.find((img) => img.get('type') === CONTENT_IMAGE_TYPE_KEYART_WITH_TEXT) || Map()

  if (display === TILE_V2_DISPLAY_VERTICAL) {
    image = images.find((img) => img.get('type') === CONTENT_IMAGE_TYPE_POSTER) || Map()
  }

  if (display === TILE_V2_DISPLAY_POSTER) {
    image = images.find((img) => img.get('type') === CONTENT_IMAGE_TYPE_POSTER_3X4) || Map()
  }

  if (contentType === TYPE_CONTENT_PERSON) {
    image = images.find((img) => img.get('type') === CONTENT_IMAGE_TYPE_HEADSHOT) || Map()
  }

  if (contentType === TYPE_CONTENT_TAG) {
    image = images.find((img) => img.get('type') === CONTENT_IMAGE_TYPE_KEYART_NO_TEXT) || Map()
  }

  return image.get('url', '')
}

/**
 * Get the hero image sizes (width, height)
 * @param {Object} props - The props
 * @param {Map} props.data - The content data
 * @param {String} props.display - The display type for the content
 * @param {String} props.contentType - The type of content
 * @param {Boolean} props.imageOptimization - Whether or not the image
 *  should be optimized (sized down)
 * @returns {Object} the sizes for the hero image
 */
export function getImageSizes (props) {
  const {
    data = Map(),
    display = TILE_V2_DISPLAY_DEFAULT,
    contentType,
    imageOptimization,
  } = props
  const vertical = display === TILE_V2_DISPLAY_VERTICAL
  let imageWidth = imageOptimization ? '400' : '608'
  let imageHeight = imageOptimization ? '225' : '342'

  if (vertical && data.get('verticalImage')) {
    imageWidth = '385'
    imageHeight = '539'
  }

  if (contentType === TYPE_CONTENT_PERSON) {
    imageWidth = '300'
    imageHeight = '300'
  }

  return {
    width: imageWidth,
    height: imageHeight,
  }
}

/**
 * Get the hero image sizes (width, height)
 * @param {Object} props - The props
 * @param {Map} props.data - The content data
 * @param {String} props.display - The display type for the content
 * @param {String} props.contentType - The type of content
 * @param {Boolean} props.imageOptimization - Whether or not the image
 *  should be optimized (sized down)
 * @returns {Object} the sizes for the hero image
 */
export function getImageSizesGraphql (props) {
  const {
    data = Map(),
    display = TILE_V2_DISPLAY_DEFAULT,
    contentType,
    imageOptimization,
  } = props
  const images = data.get('images', List()) || List()
  const verticalImage = images.find((img) => img.get('type') === CONTENT_IMAGE_TYPE_POSTER)
  let imageWidth = imageOptimization ? '400' : '608'
  let imageHeight = imageOptimization ? '225' : '342'

  if (display === TILE_V2_DISPLAY_VERTICAL && verticalImage) {
    imageWidth = '385'
    imageHeight = '539'
  }

  if (display === TILE_V2_DISPLAY_POSTER) {
    imageWidth = '385'
    imageHeight = '513'
  }

  if (contentType === TYPE_CONTENT_PERSON) {
    imageWidth = '300'
    imageHeight = '300'
  }

  return {
    width: imageWidth,
    height: imageHeight,
  }
}

/**
 * Get the class names for the parent tile component
 * @param {Object} props - The props
 * @param {Boolean} props.hovered - The hover state
 * @param {String} props.display - The display type of the tile (vertical, round, etc.)
 * @returns {String} Class names for the tile component
 */
export function getTileV2ClassName (props, hovered = false) {
  const { display = TILE_V2_DISPLAY_DEFAULT, contentType } = props
  const baseClassName = 'tile-v2'
  const classNames = [baseClassName]

  if (hovered) {
    classNames.push(`${baseClassName}--hovered`)
  }

  if (display) {
    classNames.push(`${baseClassName}--${display}`)
  }

  if (contentType) {
    classNames.push(`${baseClassName}--${contentType}`)
  }

  return classNames.join(' ')
}

/**
 * The class names for the tile hero components
 * @param {Object} props - The props
 * @param {String} props.display - The display type of the tile (vertical, round, etc.)
 * @param {String} props.baseClassName - The base class name (tile-v2, etc.)
 * @param {String} props.contentType - The type of content
 * @param {Boolean} props.hovered - Whether or not the tile is in a hovered state
 * @returns {String} the class name string for the tile hero components
 */
export function getTileV2ElementClassName (props) {
  const {
    display = TILE_V2_DISPLAY_DEFAULT,
    baseClassName,
    contentType,
    hovered,
  } = props
  const classNames = [baseClassName]

  if (display) {
    classNames.push(`${baseClassName}--${display}`)
  }

  if (contentType) {
    classNames.push(`${baseClassName}--${contentType}`)
  }

  if (hovered) {
    classNames.push(`${baseClassName}--hovered`)
  }

  return classNames.join(' ')
}

/**
 * The class names for the tile tags component
 * @param {Object} props - The props
 * @param {String} props.baseClassName - The base class name (tile-v2__tags, etc.)
 * @param {String} props.size - The size of the tags
 * @param {String} props.linked - Whether or not the tag has a url/path
 * @returns {String} the class name string for the tile tags component
 */
export function getTileV2TagsClass (props) {
  const {
    baseClassName,
    size,
    linked,
    type,
  } = props
  const classNames = [baseClassName]

  if (size) {
    classNames.push(`${baseClassName}--${size}`)
  }

  if (linked) {
    classNames.push(`${baseClassName}--linked`)
  }

  if (type === NODE_TAG_TYPE_NEW) {
    classNames.push(`${baseClassName}--new`)
  }

  return classNames.join(' ')
}

/**
 * GA event for tile tag click
 * @param {Object} props - The props
 * @param {String} props.contentType - The type of content
 * @param {Function} props.setEventTagClicked - Action creator function for setting the event data
 * @param {String} props.tagName - The name of the tag
 * @param {String} props.tagId - The id of the tag
 * @param {Map} props.data - The content data
 */
export function onClickTileTag (props) {
  const {
    contentType,
    setDefaultGa4Event,
    resolver,
    tagName,
    tagId,
    data,
  } = props
  const title = data.get('title', '')
  const id = data.get('id')

  // GA4 event
  const seriesId = data.get('seriesId')
  const uiLocation = getPathLocationName(resolver.get('path'))
  let ga4EventData = TAG_CLICKED_GA4_EVENT.mergeDeep({
    content_id: id,
    content_type: contentType,
    content_name: title,
    tag_name: tagName,
    tag_id: tagId,
    ui_element: 'Tile',
    ui_location: uiLocation,
  })

  if (seriesId) {
    ga4EventData = ga4EventData.mergeDeep({
      parent_series_name: data.get('seriesTitle'),
      parent_series_id: seriesId,
    })
  }

  setDefaultGa4Event(ga4EventData)
}

/**
 * Determine whether or not the user should see the Upgrade CTA for Gaia+
 * @param {Object} options - The options
 * @param {Map} options.feature - The feature for the content
 * @param {List} options.subscriptions - The user's subscriptions (entitlements)
 * @returns {Boolean} Whether or not the user should see the upgrade to watch CTA for Gaia+
 */
export function isLiveAccessUpgradeToWatch (options) {
  const { feature, subscriptions } = options

  return isLiveAccessFeature(feature) && !hasLiveAccessEntitlement(subscriptions)
}

export function getTileDisplayType (displayMode) {
  switch (displayMode) {
    case DISPLAY_MODE_TILE_LIST_5X7:
    case DISPLAY_MODE_TILE_LIST_5X7_BLUE_GRADIENT_BACKGROUND:
    case DISPLAY_MODE_TILE_LIST_5X7_GRAY_BACKGROUND:
    case DISPLAY_MODE_TILE_LIST_GAIA_PLUS:
      return TILE_V2_DISPLAY_VERTICAL
    case DISPLAY_MODE_TILE_LIST_3X4:
    case DISPLAY_MODE_TILE_LIST_3X4_BLUE_GRADIENT_BACKGROUND:
    case DISPLAY_MODE_TILE_LIST_3X4_GRAY_BACKGROUND:
    case DISPLAY_MODE_TILE_LIST_TOP_TEN_3X4:
      return TILE_V2_DISPLAY_POSTER
    default:
      return TILE_V2_DISPLAY_DEFAULT
  }
}
